import { Icon } from "@mdi/react";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { mdiPlayBox, mdiTrashCan } from "@mdi/js";
import { Button, Input, Modal } from "antd";
import { extractYouTubeVideoId, isValidYouTubeUrl } from "utils/youtubeUtils";

import {
  setTutorialKeywords,
  useCreateTutorial,
  useDeleteTutorial,
  useUpdateTutorial
} from "api/tutorials";

import { formatKeywords, parseKeywords } from "../../keywords";
import styles from "../../styles/tutorialViews.module.scss";
import { Category, TutorialItem } from "../../types";
import CategorySelect from "../CategorySelect";

interface TutorialEditViewProps {
  tutorial?: TutorialItem;
  onCancel: () => void;
  onSuccess: (wasDeleted?: boolean) => void;
  isNew?: boolean;
}

const TutorialEditView: React.FC<TutorialEditViewProps> = ({
  tutorial,
  onCancel,
  onSuccess,
  isNew = false
}) => {
  const getEmptyForm = (): Partial<TutorialItem> => ({
    title: "",
    videoUrl: "",
    categories: undefined,
    order: undefined,
    description: ""
  });

  const [formData, setFormData] = useState<TutorialItem>(() => {
    return tutorial ? { ...tutorial } : (getEmptyForm() as TutorialItem);
  });

  // Held as raw text so the admin can type freely; converted to a list on save.
  const [keywordText, setKeywordText] = useState(() =>
    formatKeywords(tutorial?.keywords)
  );

  const queryClient = useQueryClient();
  const createMutation = useCreateTutorial();
  const updateMutation = useUpdateTutorial();
  const deleteMutation = useDeleteTutorial();

  const isLoading =
    createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading;

  // Reset form when tutorial prop changes
  useEffect(() => {
    if (tutorial) {
      setFormData({ ...tutorial });
      setKeywordText(formatKeywords(tutorial.keywords));
    } else {
      setFormData(getEmptyForm() as TutorialItem);
      setKeywordText("");
    }
  }, [tutorial]);

  const handleInputChange = (field: keyof TutorialItem, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryChange = (categories: Category[]) => {
    setFormData((prev) => ({
      ...prev,
      categories: categories
    }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.videoUrl.trim()) {
      toast.error("Video URL is required");
      return;
    }

    if (!isValidYouTubeUrl(formData.videoUrl)) {
      toast.error("Please enter a valid YouTube URL");
      return;
    }

    const requestData = {
      title: formData.title,
      videoUrl: formData.videoUrl,
      categories: formData.categories,
      order: formData.order,
      description: formData.description
    };
    try {
      let tutorialId = formData.id;
      if (isNew) {
        const created = await createMutation.mutateAsync(requestData);
        tutorialId = created?.id ?? tutorialId;
        toast.success("Tutorial created successfully");
      } else {
        await updateMutation.mutateAsync({ id: formData.id, data: requestData });
        toast.success("Tutorial updated successfully");
      }

      // Keywords are stored separately from the video's details, so they need
      // their own call. Failing to save them should not make the whole save look
      // like it failed, since the video itself was saved.
      if (tutorialId) {
        try {
          await setTutorialKeywords(tutorialId, parseKeywords(keywordText));
        } catch (error) {
          toast.error("The video was saved, but its keywords were not.");
        }
      } else {
        // Without an id the keywords have nowhere to go, and saying nothing
        // would let the admin believe they had been stored.
        toast.error("The video was saved, but its keywords were not.");
      }

      // Refreshed here rather than when the video itself was saved: doing it
      // first fetched a copy without the keywords, which the form then wrote
      // back as empty on the next save.
      await queryClient.invalidateQueries(["tutorials"]);

      onSuccess();
    } catch (error) {
      toast.error(`Failed to ${isNew ? "create" : "update"} tutorial. Please try again.`);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Tutorial?",
      content:
        "Are you sure you want to delete this tutorial? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(formData.id);
          toast.success("Tutorial deleted successfully");
          onSuccess(true); // Pass true to indicate deletion
        } catch (error) {
          toast.error("Failed to delete tutorial. Please try again.");
        }
      }
    });
  };

  return (
    <div className={styles.tutorialEditView} data-testid="tutorial-edit-view">
      <div className={styles.headerContainer}>
        <div className={styles.titleContainer}>
          <h2>{isNew ? "Create New Tutorial" : "Edit Tutorial"}</h2>
          {!isNew && (
            <Button
              type="text"
              onClick={handleDelete}
              disabled={isLoading}
              className={styles.deleteButton}
              data-testid="tutorial-form-delete-button">
              <Icon path={mdiTrashCan} size="2.5rem" />
            </Button>
          )}
        </div>
        <div className={styles.actionsContainer}>
          <Button
            type="primary"
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading || !formData.videoUrl.trim()}
            data-testid="tutorial-form-save-button">
            {isLoading ? "Saving..." : "Save"}
          </Button>
          <Button
            type="default"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="tutorial-form-cancel-button">
            Cancel
          </Button>
        </div>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.formGroup}>
          <label htmlFor="tutorial-title">Title *</label>
          <Input
            id="tutorial-title"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="Video Title"
            data-testid="tutorial-form-title"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tutorial-category">Categories *</label>
          <CategorySelect
            id="tutorial-category"
            value={formData.categories}
            onChange={handleCategoryChange}
            data-testid="tutorial-form-categories"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tutorial-order">Display Order</label>
          <Input
            id="tutorial-order"
            type="number"
            value={formData.order}
            onChange={(e) =>
              handleInputChange("order", parseInt(e.target.value) || undefined)
            }
            placeholder="Display order (lower numbers appear first)"
            min="0"
            data-testid="tutorial-form-order"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tutorial-video-url">Video URL *</label>
          <Input
            id="tutorial-video-url"
            value={formData.videoUrl}
            onChange={(e) => handleInputChange("videoUrl", e.target.value)}
            placeholder="Enter video URL"
            data-testid="tutorial-form-video-url"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tutorial-keywords">Search keywords</label>
          <Input.TextArea
            id="tutorial-keywords"
            value={keywordText}
            onChange={(e) => setKeywordText(e.target.value)}
            placeholder={
              "One per line, or comma separated. Words customers would search for that the video itself never says — for example “resource play” for a video about basins, or “cross section” for one about XDA."
            }
            rows={3}
            data-testid="tutorial-form-keywords"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tutorial-description">Description</label>
          <Input.TextArea
            id="tutorial-description"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="What does this video cover? Users search this text, so include the terms they would type — both the words used in the video and the ones customers use for the same thing."
            rows={4}
            data-testid="tutorial-form-description"
          />
        </div>

        <div className={styles.videoContainer}>
          {(() => {
            const videoId = extractYouTubeVideoId(formData.videoUrl);
            return videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                width="100%"
                allowFullScreen
                data-testid="tutorial-video-preview"
                title={formData.title || "Tutorial Video Preview"}></iframe>
            ) : formData.videoUrl.trim() ? (
              <div className={styles.videoPlaceholder}>
                <Icon path={mdiPlayBox} />
                <p>Invalid URL</p>
              </div>
            ) : (
              <div className={styles.videoPlaceholder}>
                <Icon path={mdiPlayBox} />
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default TutorialEditView;
